import React, { useState } from "react";
import { WizardProvider, useWizardState } from "./hooks/useWizardState";
import { StepIndicator } from "./components/wizard/StepIndicator";
import { NavigationButtons } from "./components/wizard/NavigationButtons";
import { StepContainer } from "./components/wizard/StepContainer";
import { WelcomeScreen } from "./screens/WelcomeScreen";
import { Step1Essentials } from "./screens/Step1Essentials";
import { Step2Travelers } from "./screens/Step2Travelers";
import { Step3Style } from "./screens/Step3Style";
import { Step4Priorities } from "./screens/Step4Priorities";
import { Step5Contact } from "./screens/Step5Contact";
import { SuccessScreen } from "./components/ui/SuccessScreen";
import { ErrorToast } from "./components/ui/ErrorToast";
import { submitQuote } from "./services/firestore";
import { TenantProvider, useTenant } from "./tenant/TenantContext";
import type { TenantConfig } from "./tenant/tenants.config";
import "./styles/globals.css";
import "./styles/animations.css";
import "./styles/tailux-forms.css";

const WizardContent: React.FC = () => {
  const {
    currentStep,
    formData,
    direction,
    goToNextStep,
    goToPreviousStep,
    goToStep,
    isStepValid,
  } = useWizardState();
  const tenant = useTenant();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string>("");

  const handleSubmit = async () => {
    if (!isStepValid("step5")) {
      return;
    }

    setIsSubmitting(true);
    setError("");

    try {
      const result = await submitQuote(formData, tenant.meta.corporateOrganization);
      if (result.success) {
        goToStep("success");
      } else {
        setError(result.error || "An error occurred. Please try again.");
        setIsSubmitting(false);
      }
    } catch (err) {
      setError("An error occurred. Please try again.");
      setIsSubmitting(false);
    }
  };

  const handleNext = () => {
    if (currentStep === "step5") {
      handleSubmit();
    } else {
      goToNextStep();
    }
  };

  const showProgressBar = currentStep !== "welcome" && currentStep !== "success" && currentStep !== "error";
  const showNavigation = currentStep !== "welcome" && currentStep !== "success" && currentStep !== "error";

  return (
    <div className="app">
      <main className="app-main">
        {showProgressBar && <StepIndicator />}

        {currentStep === "welcome" && (
          <StepContainer direction="forward" isActive={true}>
            <WelcomeScreen onStart={() => goToNextStep()} />
          </StepContainer>
        )}

        {currentStep === "step1" && (
          <StepContainer direction={direction} isActive={true}>
            <Step1Essentials />
          </StepContainer>
        )}

        {currentStep === "step2" && (
          <StepContainer direction={direction} isActive={true}>
            <Step2Travelers />
          </StepContainer>
        )}

        {currentStep === "step3" && (
          <StepContainer direction={direction} isActive={true}>
            <Step3Style />
          </StepContainer>
        )}

        {currentStep === "step4" && (
          <StepContainer direction={direction} isActive={true}>
            <Step4Priorities />
          </StepContainer>
        )}

        {currentStep === "step5" && (
          <StepContainer direction={direction} isActive={true}>
            <Step5Contact />
          </StepContainer>
        )}

        {currentStep === "success" && (
          <StepContainer direction="forward" isActive={true}>
            <SuccessScreen />
          </StepContainer>
        )}

      {showNavigation && (
        <NavigationButtons
          onNext={handleNext}
          onBack={goToPreviousStep}
          canGoNext={isStepValid(currentStep)}
          canGoBack={currentStep !== "step1"}
          isLastStep={currentStep === "step5"}
          isLoading={isSubmitting}
        />
      )}
      </main>

      {error && <ErrorToast message={error} onDismiss={() => setError("")} />}
    </div>
  );
};

interface AppProps {
  tenantConfig: TenantConfig;
}

const App: React.FC<AppProps> = ({ tenantConfig }) => {
  return (
    <TenantProvider config={tenantConfig}>
      <WizardProvider>
        <WizardContent />
      </WizardProvider>
    </TenantProvider>
  );
};

export default App;

